import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {BaseModalComponent} from "@app/_components/base-modal/base-modal.component";
import {EdiFileSummaryFilter, EdiFileSummaryResponse} from "@app/_models/api";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {NgbDateStruct, NgbInputDatepicker, NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {ToastService} from "@app/_services/toast.service";

@Component({
    selector: 'app-edi-file-summary-filter',
    standalone: true,
    imports: [ReactiveFormsModule, NgClass, NgbInputDatepicker],
    templateUrl: './edi-file-summary-filter.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './edi-file-summary-filter.component.scss'
})
export class EdiFileSummaryFilterComponent extends BaseModalComponent implements OnInit {
    @Input() customerId: string | undefined;
    @Input() tradingPartnerName: string | undefined;
    @Output() filterEvent = new EventEmitter<EdiFileSummaryFilter>();
    @Output() filterResponseEvent = new EventEmitter<EdiFileSummaryResponse[]>();
    @Output() isCustomFilterApplied = new EventEmitter<boolean>();

    @ViewChild('modal') modal!: TemplateRef<EdiFileSummaryFilterComponent>;

    filter: EdiFileSummaryFilter = {};
    defaultFilter: EdiFileSummaryFilter = {};
    hasMaintenanceIssue: boolean = false;

    constructor(protected modalService: NgbModal, private toastService: ToastService) {
        super();
        this.formGroup = new FormGroup({
            fileName: new FormControl<string | null>(null),
            reportingStartDate: new FormControl<string | null>(null),
            reportingEndDate: new FormControl<string | null>(null),
            loadStartDate: new FormControl<string | null>(null),
            loadEndDate: new FormControl<string | null>(null),
            hasMaintenanceIssue: new FormControl<boolean>(false, {nonNullable: true}),
        });
    }

    ngOnInit(): void {
        this.defaultFilter = this.buildScopeFilter();
        this.filter = {...this.defaultFilter};
    }

    onOpen() {
        this.modalRef = this.modalService.open(this.modal, this.modalOptions);
    }

    onClose() {
        this.modalRef.close();
    }

    onSubmit() {
        const formData = this.formGroup.value;
        this.savingData = true;

        this.filter = {
            ...this.buildScopeFilter(),
            fileName: formData.fileName ?? null,
            reportingStartDate: this.formatNgbDate(formData.reportingStartDate),
            reportingEndDate: this.formatNgbDate(formData.reportingEndDate),
            loadStartDate: this.formatNgbDate(formData.loadStartDate),
            loadEndDate: this.formatNgbDate(formData.loadEndDate),
            maintenanceIssuesOnly: formData.hasMaintenanceIssue,
        };
        this.hasMaintenanceIssue = formData.hasMaintenanceIssue;

        this.applyFilter();
    }

    onReset() {
        this.filter = {...this.buildScopeFilter()};
        this.hasMaintenanceIssue = false;
        this.resetFormGroup();
        this.applyFilter(true);
        this.modalRef.close();
    }

    isFilterApplied(): boolean {
        return !!this.filter && (
            !!this.filter.fileName ||
            !!this.filter.reportingStartDate ||
            !!this.filter.reportingEndDate ||
            !!this.filter.loadStartDate ||
            !!this.filter.loadEndDate ||
            this.hasMaintenanceIssue
        );
    }

    private buildScopeFilter(): EdiFileSummaryFilter {
        return {
            customerId: this.customerId ?? null,
            tradingPartnerName: this.tradingPartnerName ?? null,
        };
    }

    private applyFilter(reset: boolean = false) {
        this.filterEvent.emit(this.filter);
        this.isCustomFilterApplied.emit(this.isFilterApplied());
        this.toastService.showSuccess("Success", reset ? "Filter Reset" : "Filter Applied");
        this.savingData = false;
        this.modalRef.close();
    }

    private formatNgbDate(date?: NgbDateStruct | null) {
        return !!date ? `${date.year}-${date.month}-${date.day}` : null;
    }
}
